import { useEffect, useRef, type ReactNode } from "react";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame } from "@/components/data";

import { OptionLink } from "../links";

/** Chart colours, all from the brand token layer. */
export const CHART = {
  purple: "var(--color-ks-purple)",
  purple1: "var(--color-ks-purple-1)",
  purple2: "var(--color-ks-purple-2)",
  grey1: "var(--color-ks-grey-1)",
  grey4: "var(--color-ks-grey-4)",
  ink: "var(--color-ks-ink)",
  danger: "var(--color-ks-danger)",
  warn: "var(--color-ks-warn)",
  surface: "var(--color-ks-surface)",
} as const;

/* The brand has no categorical set, and 5 purples + 3 greys read as one colour
   (Ioan, 30 Oct). So the series are hue turns of the brand purple at an even
   lightness: brand purple first, then each next hue as far from the last as it
   can be. Second round is lighter, for criteria lists past 12. */
const HUE_TURNS = [0, 180, 90, 270, 45, 225, 135, 315, 20, 200, 110, 290] as const;
const turn = (deg: number, l: number, c: number) => `oklch(from var(--color-ks-purple) ${l} ${c} calc(h + ${deg}))`;

export const CRITERIA_COLOURS = [
  "var(--color-ks-purple)",
  ...HUE_TURNS.slice(1).map((deg) => turn(deg, 0.6, 0.13)),
  ...HUE_TURNS.map((deg) => turn(deg, 0.76, 0.1)),
] as const;

export const criteriaColour = (index: number): string =>
  CRITERIA_COLOURS[index % CRITERIA_COLOURS.length];

export const TOOLTIP_STYLE = {
  background: CHART.surface,
  border: `1px solid ${CHART.grey4}`,
  borderRadius: "var(--radius-ks-sm)",
  fontSize: 12,
};

/** A chart is at least 900px wide and grows by `slot` px per bar, then scrolls sideways. */
export const chartWidth = (count: number, slot: number) => Math.max(900, count * slot + 50);

/** Sideways scroll for a wide chart; `toEnd` opens it on the latest period. */
export function ScrollChart({ width, toEnd = false, children }: { width: number; toEnd?: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (toEnd && ref.current) ref.current.scrollLeft = ref.current.scrollWidth;
  }, [toEnd, width]);
  return (
    <div ref={ref} className="overflow-x-auto">
      <div style={{ minWidth: width }}>{children}</div>
    </div>
  );
}

export interface BarPoint {
  label: string;
  value: number | null;
  /** Longer text for the tooltip, when the axis label is short. */
  hint?: string;
  /** Set when the bar is an option, so its label links to the option. */
  option?: { optionId: string; itemId: string };
  /** Drawn in ink, not purple: the option a link came from (Option Overview's Offers button). */
  marked?: boolean;
}

export interface Reference {
  value: number;
  label: string;
  tone: "ink" | "warn";
  dashed: boolean;
  width: number;
}

interface TickProps {
  x?: number | string;
  y?: number | string;
  index?: number;
  payload?: { value?: string | number };
}

/** An axis label: upright or turned 45 degrees, and a link when it names an option. */
export function AxisTick({
  x,
  y,
  index,
  payload,
  rotate,
  links,
  from,
}: TickProps & { rotate: boolean; links: Array<BarPoint["option"]>; from?: string }) {
  const option = index === undefined ? undefined : links[index];
  const text = String(payload?.value ?? "");
  return (
    <g transform={`translate(${Number(x ?? 0)},${Number(y ?? 0) + 10})`}>
      <text
        transform={rotate ? "rotate(-45)" : undefined}
        textAnchor={rotate ? "end" : "middle"}
        fontSize={10}
        fill={CHART.grey1}
      >
        {option && from ? (
          <OptionLink optionId={option.optionId} itemId={option.itemId} from={from}>
            {text}
          </OptionLink>
        ) : (
          text
        )}
      </text>
    </g>
  );
}

/** Five evenly spaced y-axis values from 0 to the top, as the mockup draws its grid. */
export const axisTicks = (top: number, bottom = 0): number[] => [0, 1, 2, 3, 4].map((step) => bottom + ((top - bottom) * step) / 4);

/** A y-axis value as the mockup prints it: through the chart's format, or as a whole number. */
export const axisLabel = (value: number, format?: (value: number) => string): string =>
  format ? format(value) : String(Math.round(value));

/** The label of a reference line: above the line at its left end, on a halo so it reads over the bars. */
export function ReferenceLabel({ viewBox, text }: { viewBox?: { x?: number; y?: number }; text: string }) {
  return (
    <text
      x={Number(viewBox?.x ?? 0) + 6}
      y={Number(viewBox?.y ?? 0) - 6}
      textAnchor="start"
      fontSize={11}
      fill={CHART.ink}
      stroke={CHART.surface}
      strokeWidth={4}
      strokeLinejoin="round"
      paintOrder="stroke"
    >
      {text}
    </text>
  );
}

/** Room under the x axis for its labels: a turned label needs about 4px of height per character. */
export function axisHeight(rotate: boolean, labels: string[] = []): number {
  if (!rotate) return 24;
  const longest = Math.max(0, ...labels.map((label) => label.length));
  return Math.min(150, Math.max(48, 18 + longest * 4));
}

/** The highest value on the y axis: 12% above the tallest bar or the reference line. */
export function axisTop(points: BarPoint[], reference?: Reference, max?: number): number {
  if (max !== undefined) return max;
  return Math.max(1, ...points.map((point) => point.value ?? 0), reference?.value ?? 0) * 1.12;
}

export interface VBarsProps {
  points: BarPoint[];
  /** Turn the labels 45 degrees (long names, many bars). */
  rotate?: boolean;
  /** Label every n-th bar. */
  every?: number;
  format?: (value: number) => string;
  /** Fix the top of the y axis (100 for a percentage). */
  max?: number;
  reference?: Reference;
  /** The tab the option links come from, for the Back link. */
  from?: string;
  /** Only tests pass these: inside `ChartFrame` the chart takes its size from the frame. */
  width?: number;
  height?: number;
}

/** Vertical bars with a value on each bar, as drawn across mockup v6. */
export function VBarsChart({ points, rotate = false, every = 1, format, max, reference, from, width, height }: VBarsProps) {
  const links = points.map((point) => point.option);
  const show = format ?? String;
  const top = axisTop(points, reference, max);
  return (
    <BarChart data={points} width={width} height={height} margin={{ top: 18, right: 10, bottom: 0, left: 0 }}>
      <CartesianGrid vertical={false} stroke={CHART.grey4} />
      <XAxis
        dataKey="label"
        interval={every - 1}
        height={axisHeight(rotate, points.map((point) => point.label))}
        tickLine={false}
        stroke={CHART.grey1}
        tick={(props: TickProps) => <AxisTick {...props} rotate={rotate} links={links} from={from} />}
      />
      <YAxis
        width={44}
        tickLine={false}
        axisLine={false}
        stroke={CHART.grey1}
        tick={{ fontSize: 10 }}
        tickFormatter={(value: number) => axisLabel(value, format)}
        domain={[0, top]}
        ticks={axisTicks(top)}
      />
      <Tooltip
        cursor={false}
        contentStyle={TOOLTIP_STYLE}
        formatter={(value) => show(Number(value))}
        labelFormatter={(label, payload) => (payload?.[0]?.payload as BarPoint | undefined)?.hint ?? String(label)}
      />
      <Bar dataKey="value" name="Value" fill={CHART.purple} isAnimationActive={false}>
        {points.some((point) => point.marked)
          ? points.map((point, index) => <Cell key={index} fill={point.marked ? CHART.ink : CHART.purple} />)
          : null}
        <LabelList
          dataKey="value"
          position="top"
          fontSize={10}
          fill={CHART.ink}
          formatter={(value) => (value == null ? "" : show(Number(value)))}
        />
      </Bar>
      {reference ? (
        <ReferenceLine
          y={reference.value}
          stroke={reference.tone === "warn" ? CHART.warn : CHART.ink}
          strokeWidth={reference.width}
          strokeDasharray={reference.dashed ? "6 4" : undefined}
          label={(props: { viewBox?: { x?: number; y?: number } }) => <ReferenceLabel viewBox={props.viewBox} text={reference.label} />}
        />
      ) : null}
    </BarChart>
  );
}

/** `VBarsChart` in the shell chart frame, as wide as its bars need, scrolling sideways. */
export function VBars({
  title,
  slot,
  toEnd = false,
  height = 260,
  ...chart
}: VBarsProps & { title: string; slot: number; toEnd?: boolean }) {
  return (
    <ScrollChart width={chartWidth(chart.points.length, slot)} toEnd={toEnd}>
      <ChartFrame title={title} height={height} pointCount={chart.points.filter((point) => point.value != null).length}>
        <VBarsChart {...chart} />
      </ChartFrame>
    </ScrollChart>
  );
}
